import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Lock, User, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SectionCardProps {
  section: {
    id: string;
    name: string;
    tasksTotal: number;
    tasksCompleted: number;
    assignedTo?: string;
    allowCrossAccess: boolean;
    isAssignedToMe: boolean;
  };
  isWorkStarted: boolean;
  onClick: () => void;
}

export const SectionCard = ({ section, isWorkStarted, onClick }: SectionCardProps) => {
  const progressPercent = section.tasksTotal > 0 
    ? Math.round((section.tasksCompleted / section.tasksTotal) * 100) 
    : 0;

  // Location-based access: after check-in the employee can work in every section of this property.
  const isPreview = !isWorkStarted;
  const isCompleted = progressPercent === 100;

  return (
    <Card 
      className={cn(
        "shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer group",
        "bg-white dark:bg-zinc-900/80",
        "border-2 border-zinc-200/60 dark:border-zinc-700/50",
        "hover:border-primary/40 dark:hover:border-primary/40",
        isPreview && "opacity-80",
        isCompleted && "border-l-4 border-l-green-500 dark:border-l-green-400"
      )}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800">
                <Building2 className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <h3 className="truncate text-base font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                {section.name}
              </h3>
            </div>
            
            {/* Assigned to badge */}
            {section.assignedTo && !section.isAssignedToMe && (
              <div className="ml-11 mb-2">
                <Badge variant="outline" className="text-xs font-normal gap-1">
                  <User className="h-3 w-3" />
                  Przypisane do: {section.assignedTo}
                </Badge>
              </div>
            )}
            
            {/* Progress bar */}
            <div className="ml-11 flex items-center gap-3">
              <div className="flex-1 h-2 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all",
                    progressPercent === 100 
                      ? "bg-green-500 dark:bg-green-400" 
                      : "bg-primary"
                  )}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                {section.tasksCompleted}/{section.tasksTotal}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isPreview && (
              <Lock className="h-4 w-4 text-muted-foreground" />
            )}
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
